import React, {
  createContext, ReactNode, useContext, useMemo, useState,
} from 'react';

// item 61 — the SKIN: which visual personality the app wears, chosen per-device on
// Tools → Appearance and persisted in localStorage.
//  - basix       → the Aurora "sexy web3" look (glass, glow, gradient, motion)
//  - accountant  → a typewriter LEDGER skin: Courier Prime monospace, hard ink
//                  strokes, fully monochrome (no brand accent, no glow). "An
//                  accounting ledger on a typewriter."
//
// The Accountant skin additionally carries its own PAPER tone (the ledgerTone) —
// four typed-paper finishes that replace the generic Light/Dark colour mode while
// that skin is active:
//  - brilliant → plain bright-white paper, black type (a fresh sheet in the platen)
//  - soft      → off-cream paper, soft ink (aged ledger stock)
//  - softDark  → warm medium-dark paper, cream type (dimmed, easy on the eyes)
//  - dark      → deep charcoal paper, cream type (true dark)
// brilliant/soft are LIGHT-mode tones, softDark/dark are DARK-mode tones — the tone
// drives Chakra's colour mode while Accountant is active (see AppearanceProvider).
//
// This lives in its OWN tiny, Chakra-free provider mounted ABOVE BrandThemeProvider
// (which BUILDS the Chakra theme) — the theme branches on skin + tone (font, paper,
// strokes, flat mono buttons/menus), so they cannot live in AppearanceProvider: that
// sits *inside* the ChakraProvider. Mirrors BrandSourceProvider exactly.

export type Skin = 'basix' | 'accountant';
export type LedgerTone = 'brilliant' | 'soft' | 'softDark' | 'dark';
// The Basix (Aurora) canvas preference. 'light'/'dark' are the original bright-white
// and deep-dark canvases; 'softLight'/'softDark' add two lower-glare tones in between
// (a muted off-white and a soft slate); 'system' tracks the OS. This is owned HERE,
// above the ChakraProvider — NOT in AppearanceProvider — because the soft tones change
// the BUILT theme's canvas colour, and the theme is built by BrandThemeProvider which
// sits above AppearanceProvider. (light/softLight are LIGHT-mode, softDark/dark are
// DARK-mode; AppearanceProvider pins Chakra's colour mode to match.) Mirrors ledgerTone.
export type ColorPref = 'light' | 'softLight' | 'softDark' | 'dark' | 'system';

const SKIN_KEY = 'basix.appearance.skin';
const TONE_KEY = 'basix.appearance.ledgerTone';
const COLOR_KEY = 'basix.appearance.colorPref';
const SKINS: Skin[] = ['basix', 'accountant'];
const TONES: LedgerTone[] = ['brilliant', 'soft', 'softDark', 'dark'];
const COLOR_PREFS: ColorPref[] = ['light', 'softLight', 'softDark', 'dark', 'system'];

interface SkinContextValue {
  skin: Skin;
  setSkin: (s: Skin) => void;
  ledgerTone: LedgerTone;
  setLedgerTone: (t: LedgerTone) => void;
  colorPref: ColorPref;
  setColorPref: (c: ColorPref) => void;
}

const SkinContext = createContext<SkinContextValue>({
  skin: 'basix',
  setSkin: () => {},
  ledgerTone: 'soft',
  setLedgerTone: () => {},
  colorPref: 'dark',
  setColorPref: () => {},
});

const read = <T extends string>(key: string, fallback: T, allowed: T[]): T => {
  try {
    const v = window.localStorage.getItem(key) as T | null;
    return v && allowed.includes(v) ? v : fallback;
  } catch {
    return fallback;
  }
};

export const SkinProvider = ({ children }: { children: ReactNode }) => {
  const [skin, setSkinState] = useState<Skin>(() => read(SKIN_KEY, 'basix', SKINS));
  const [ledgerTone, setToneState] = useState<LedgerTone>(() => read(TONE_KEY, 'soft', TONES));
  const [colorPref, setColorPrefState] = useState<ColorPref>(
    () => read(COLOR_KEY, 'dark', COLOR_PREFS),
  );

  const setSkin = (s: Skin) => {
    setSkinState(s);
    try { window.localStorage.setItem(SKIN_KEY, s); } catch { /* ignore */ }
  };
  const setLedgerTone = (t: LedgerTone) => {
    setToneState(t);
    try { window.localStorage.setItem(TONE_KEY, t); } catch { /* ignore */ }
  };
  const setColorPref = (c: ColorPref) => {
    setColorPrefState(c);
    try { window.localStorage.setItem(COLOR_KEY, c); } catch { /* ignore */ }
  };

  const value = useMemo<SkinContextValue>(
    () => ({
      skin, setSkin, ledgerTone, setLedgerTone, colorPref, setColorPref,
    }),
    [skin, ledgerTone, colorPref],
  );

  return <SkinContext.Provider value={value}>{children}</SkinContext.Provider>;
};

export const useSkin = (): SkinContextValue => useContext(SkinContext);

export default SkinProvider;
